import React from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import Dashboard from "./pages/Dashboard";
import AddressProfile from "./pages/AddressProfile";
import SubmitReview from "./pages/SubmitReview";
import QuickReviewUpdate from "./pages/QuickReviewUpdate";
import ReviewSuccess from "./pages/ReviewSuccess";
import Profile from "./pages/Profile";
import ReviewHistory from "./pages/ReviewHistory";
import AccountSettings from "./pages/AccountSettings";
import SearchPage from "./pages/SearchPage";
import PropertyDetails from "./pages/PropertyDetails";
import VerificationSettings from "./pages/VerificationSettings";
import AdminVerifications from "./pages/AdminVerifications";
import InvitePage from "./pages/InvitePage";

// Components
import FeedbackButton from "./components/FeedbackButton";
import AttestationModal from "./components/AttestationModal";
import LookupLimitModal from "./components/LookupLimitModal";

// Admin Pages
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboardHome from "./pages/admin/AdminDashboardHome";
import AdminReviews from "./pages/admin/AdminReviews";
import AdminModeration from "./pages/admin/AdminModeration";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminOwnership from "./pages/admin/AdminOwnership";
import AdminDeletions from "./pages/admin/AdminDeletions";
import AdminAudit from "./pages/admin/AdminAudit";
import AdminInviteCodes from "./pages/admin/AdminInviteCodes";
import AdminFeedback from "./pages/admin/AdminFeedback";

// Legal Pages
import TermsOfService from "./pages/legal/TermsOfService";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import AcceptableUsePolicy from "./pages/legal/AcceptableUsePolicy";
import ContentGuidelines from "./pages/legal/ContentGuidelines";
import CommunityStandards from "./pages/legal/CommunityStandards";
import OwnershipResetPolicy from "./pages/legal/OwnershipResetPolicy";
import About from "./pages/legal/About";
import Contact from "./pages/legal/Contact";

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin, requireModerator }) => {
    const { user, loading, isAdmin, isModerator } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="loader"></div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (requireAdmin && !isAdmin) {
        return <Navigate to="/dashboard" replace />;
    }

    if (requireModerator && !isModerator) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

// Admin Protected Route - Redirects to admin login if not admin
const AdminProtectedRoute = ({ children }) => {
    const { user, loading, isAdmin, isModerator } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="loader"></div>
            </div>
        );
    }

    // If not logged in or not admin/moderator, redirect to admin login
    if (!user || (!isAdmin && !isModerator)) {
        return <Navigate to="/admin/login" replace />;
    }

    return children;
};

// Public Route - Redirect to dashboard if logged in
const PublicRoute = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="loader"></div>
            </div>
        );
    }

    if (user) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route 
                path="/" 
                element={
                    <PublicRoute>
                        <Landing />
                    </PublicRoute>
                } 
            />
            <Route 
                path="/login" 
                element={
                    <PublicRoute>
                        <Login />
                    </PublicRoute>
                } 
            />
            <Route 
                path="/signup" 
                element={
                    <PublicRoute>
                        <Signup />
                    </PublicRoute>
                } 
            />
            <Route path="/invite/:code" element={<InvitePage />} />
            <Route 
                path="/forgot-password" 
                element={
                    <PublicRoute>
                        <ForgotPassword />
                    </PublicRoute>
                } 
            />
            <Route 
                path="/reset-password" 
                element={<ResetPassword />} 
            />
            <Route 
                path="/verify-email" 
                element={<VerifyEmail />} 
            />

            {/* Protected Routes */}
            <Route 
                path="/dashboard" 
                element={
                    <ProtectedRoute>
                        <Dashboard />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/search" 
                element={
                    <ProtectedRoute>
                        <SearchPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/address/:id" 
                element={
                    <ProtectedRoute>
                        <PropertyDetails />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/property/:id" 
                element={
                    <ProtectedRoute>
                        <PropertyDetails />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/review/new/:addressId" 
                element={
                    <ProtectedRoute>
                        <SubmitReview />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/review/success/:addressId" 
                element={
                    <ProtectedRoute>
                        <ReviewSuccess />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/review/update/:reviewId" 
                element={
                    <ProtectedRoute>
                        <QuickReviewUpdate />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/profile" 
                element={
                    <ProtectedRoute>
                        <Profile />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/settings" 
                element={
                    <ProtectedRoute>
                        <AccountSettings />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/reviews/history" 
                element={
                    <ProtectedRoute>
                        <ReviewHistory />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/settings/verification" 
                element={
                    <ProtectedRoute>
                        <VerificationSettings />
                    </ProtectedRoute>
                } 
            />

            {/* Moderator Routes */}
            <Route 
                path="/moderation" 
                element={
                    <AdminProtectedRoute>
                        <AdminModeration />
                    </AdminProtectedRoute>
                } 
            />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route 
                path="/admin" 
                element={
                    <AdminProtectedRoute>
                        <AdminDashboardHome />
                    </AdminProtectedRoute>
                } 
            />
            <Route 
                path="/admin/reviews" 
                element={
                    <AdminProtectedRoute>
                        <AdminReviews />
                    </AdminProtectedRoute>
                } 
            />
            <Route 
                path="/admin/moderation" 
                element={
                    <AdminProtectedRoute>
                        <AdminModeration />
                    </AdminProtectedRoute>
                } 
            />
            <Route 
                path="/admin/users" 
                element={
                    <AdminProtectedRoute>
                        <AdminUsers />
                    </AdminProtectedRoute>
                } 
            />
            <Route 
                path="/admin/verifications" 
                element={
                    <AdminProtectedRoute>
                        <AdminVerifications />
                    </AdminProtectedRoute>
                } 
            />
            <Route 
                path="/admin/ownership" 
                element={
                    <AdminProtectedRoute>
                        <AdminOwnership />
                    </AdminProtectedRoute>
                } 
            />
            <Route 
                path="/admin/deletions" 
                element={
                    <AdminProtectedRoute>
                        <AdminDeletions />
                    </AdminProtectedRoute>
                } 
            />
            <Route 
                path="/admin/audit" 
                element={
                    <AdminProtectedRoute>
                        <AdminAudit />
                    </AdminProtectedRoute>
                } 
            />
            <Route 
                path="/admin/invite-codes" 
                element={
                    <AdminProtectedRoute>
                        <AdminInviteCodes />
                    </AdminProtectedRoute>
                } 
            />
            <Route 
                path="/admin/feedback" 
                element={
                    <AdminProtectedRoute>
                        <AdminFeedback />
                    </AdminProtectedRoute>
                } 
            />

            {/* Legal Pages - Public */}
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/acceptable-use" element={<AcceptableUsePolicy />} />
            <Route path="/content-guidelines" element={<ContentGuidelines />} />
            <Route path="/community-standards" element={<CommunityStandards />} />
            <Route path="/ownership-reset-policy" element={<OwnershipResetPolicy />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />

            {/* Catch all - redirect to landing */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

// Attestation Modal Wrapper - must be inside AuthProvider to access useAuth
function AttestationWrapper() {
    const { showAttestationModal, handleAttestationAccept } = useAuth();
    
    if (!showAttestationModal) return null;
    
    return <AttestationModal onAccept={handleAttestationAccept} />;
}

// Lookup Limit Modal Wrapper - listens for lookup-limit-reached events
function LookupLimitWrapper() {
    const [showModal, setShowModal] = React.useState(false);
    const [hoursRemaining, setHoursRemaining] = React.useState(24);
    
    React.useEffect(() => {
        const handleLookupLimit = (event) => {
            setHoursRemaining(event.detail?.hoursRemaining || 24);
            setShowModal(true);
        };
        
        window.addEventListener('lookup-limit-reached', handleLookupLimit);
        return () => {
            window.removeEventListener('lookup-limit-reached', handleLookupLimit);
        };
    }, []);
    
    if (!showModal) return null;
    
    return <LookupLimitModal hoursRemaining={hoursRemaining} onClose={() => setShowModal(false)} />;
}

function App() {
    return (
        <div className="App">
            <ThemeProvider>
                <AuthProvider>
                    <BrowserRouter>
                        <AppRoutes />
                        <FeedbackButton />
                        <AttestationWrapper />
                        <LookupLimitWrapper />
                        <Toaster 
                            position="top-right" 
                            richColors
                            toastOptions={{
                                className: 'theme-transition',
                            }}
                        />
                    </BrowserRouter>
                </AuthProvider>
            </ThemeProvider>
        </div>
    );
}

export default App;
